import { PerspectiveCamera, Vector3 } from 'three/webgpu';

const PIVOT_HEIGHT = 4.2;
const DISTANCE = 8;
const SHOULDER = 2.0;
const FOLLOW_RATE = 14;
/** How far the camera stays off a wall it would otherwise clip through. */
const CLIP_MARGIN = 0.6;
const MIN_DISTANCE = 1.5;

/** Distance to the first obstacle from `origin` along the unit `direction`, or null if none. */
export type CameraClip = (
  origin: Vector3,
  direction: Vector3,
  maxDistance: number,
) => number | null;

const _pivot = new Vector3();
const _offset = new Vector3();
const _right = new Vector3();
const _forward = new Vector3();
const _target = new Vector3();
const _look = new Vector3();
const _dir = new Vector3();

/** Over-shoulder chase camera (Section 7: FOV 75, shoulder swap on C). */
export class ChaseCamera {
  readonly camera: PerspectiveCamera;
  private shoulder = 1;
  private initialised = false;

  constructor(fov: number, aspect: number) {
    this.camera = new PerspectiveCamera(fov, aspect, 0.3, 600);
  }

  swapShoulder(): void {
    this.shoulder = -this.shoulder;
  }

  setFov(fov: number): void {
    if (this.camera.fov !== fov) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * `feet` is the mech's feet position; yaw/pitch are the aim angles; dt in seconds. `clip`
   * pulls the camera in front of arena geometry between the pivot and the camera.
   */
  update(feet: Vector3, yaw: number, pitch: number, dt: number, clip?: CameraClip): void {
    _pivot.set(feet.x, feet.y + PIVOT_HEIGHT, feet.z);
    _forward.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    _right.set(Math.cos(yaw), 0, -Math.sin(yaw));
    const cosP = Math.cos(pitch);
    _offset.set(
      Math.sin(yaw) * cosP * DISTANCE,
      Math.sin(pitch) * DISTANCE + 0.5,
      Math.cos(yaw) * cosP * DISTANCE,
    );
    _target
      .copy(_pivot)
      .add(_offset)
      .addScaledVector(_right, SHOULDER * this.shoulder);
    if (!this.initialised) {
      this.camera.position.copy(_target);
      this.initialised = true;
    } else {
      const k = 1 - Math.exp(-FOLLOW_RATE * dt);
      this.camera.position.lerp(_target, k);
    }

    if (clip) {
      _dir.copy(this.camera.position).sub(_pivot);
      const distance = _dir.length();
      if (distance > 0) {
        _dir.divideScalar(distance);
        const hit = clip(_pivot, _dir, distance + CLIP_MARGIN);
        if (hit !== null && hit - CLIP_MARGIN < distance) {
          this.camera.position
            .copy(_pivot)
            .addScaledVector(_dir, Math.max(MIN_DISTANCE, hit - CLIP_MARGIN));
        }
      }
    }

    _look
      .copy(_pivot)
      .addScaledVector(_forward, 12)
      .addScaledVector(_right, SHOULDER * this.shoulder * 0.7);
    _look.y -= Math.sin(pitch) * 8;
    this.camera.lookAt(_look);
  }
}
